import React, { useState, useEffect, useMemo } from 'react';
import {
    FiUsers, FiUserCheck, FiShield, FiUserPlus, FiSearch,
    FiFilter, FiMoreHorizontal, FiMail, FiPhone, FiCalendar,
    FiCheckCircle, FiXCircle, FiEye, FiClock, FiActivity, FiX, FiTrash2, FiAlertTriangle
} from 'react-icons/fi';
import { userService } from '../../../services/api';
import './AdminCustomers.css';

const AdminCustomers = () => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showDrawer, setShowDrawer] = useState(false);

    // Modal States
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [registerStep, setRegisterStep] = useState(1);
    const [regFormData, setRegFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        role: 'admin'
    });
    const [regLoading, setRegLoading] = useState(false);
    const [regError, setRegError] = useState('');

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const resp = await userService.getAllUsers();
            setUsers(resp.data || []);
        } catch (err) {
            console.error('Failed to fetch user directory:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const initiateDelete = (id) => {
        setUserToDelete(id);
        setShowConfirmModal(true);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;

        setIsDeleting(true);
        try {
            await userService.deleteUser(userToDelete);
            setUsers(prev => prev.filter(u => u._id !== userToDelete));
            setShowConfirmModal(false);
            setShowSuccessModal(true);
            setShowDrawer(false);

            // Auto-dismiss success modal
            setTimeout(() => {
                setShowSuccessModal(false);
                setUserToDelete(null);
            }, 2500);
        } catch (err) {
            console.error('Delete failed:', err);
            alert('Operation failed. Ensure you have administrative clearance.');
            setShowConfirmModal(false);
        } finally {
            setIsDeleting(false);
        }
    };
    
    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        if (registerStep === 1) {
            setRegisterStep(2);
            return;
        }

        setRegLoading(true);
        setRegError('');
        try {
            await userService.register(
                regFormData.fullName,
                regFormData.email,
                regFormData.password,
                'admin'
            );
            setShowRegisterModal(false);
            setRegFormData({ fullName: '', email: '', password: '', role: 'admin' });
            setRegisterStep(1);
            fetchUsers();
            setShowSuccessModal(true);
            setTimeout(() => setShowSuccessModal(false), 2000);
        } catch (err) {
            setRegError(err.message || 'Registration failed');
        } finally {
            setRegLoading(false);
        }
    };

    // Stats calculation
    const stats = useMemo(() => {
        const now = new Date();
        const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));

        return {
            total: users.length,
            admins: users.filter(u => u.role === 'admin').length,
            active: users.filter(u => u.isActive).length,
            newThisMonth: users.filter(u => new Date(u.createdAt) > thirtyDaysAgo).length
        };
    }, [users]);

    // Filtering logic
    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            const matchesSearch =
                user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesRole = roleFilter === 'all' || user.role === roleFilter;
            return matchesSearch && matchesRole;
        });
    }, [users, searchTerm, roleFilter]);

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Helper for avatar colors based on name
    const getAvatarColor = (name) => {
        const colors = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'];
        const index = name.length % colors.length;
        return colors[index];
    };

    return (
        <div className="admin-customers-page">
            {/* Premium Header Section */}
            <div className="adr-cust-banner">
                <div className="adr-banner-content">
                    <span className="adr-tag">GLOBAL MEMBER HUB</span>
                    <h1>Customer Directory</h1>
                    <p>Manage and oversee your entire boutique membership ecosystem with high-density insights.</p>
                </div>
            </div>

            {/* Stats Overview - Premium Match */}
            <div className="cust-stats-grid premium">
                {/* Total Members */}
                <div className="premium-stat-card">
                    <div className="growth-badge positive">+12.5%</div>
                    <div className="premium-icon-box blue">
                        <FiUsers className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">Total Members</span>
                            <div className="p-dot blue"></div>
                        </div>
                        <span className="p-value">{stats.total}</span>
                    </div>
                    <div className="wave-bg"></div>
                </div>

                {/* Admins */}
                <div className="premium-stat-card">
                    <div className="growth-badge positive">+5%</div>
                    <div className="premium-icon-box purple">
                        <FiShield className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">Premium Admins</span>
                            <div className="p-dot purple"></div>
                        </div>
                        <span className="p-value">{stats.admins}</span>
                    </div>
                    <div className="wave-bg"></div>
                </div>

                {/* Active Users */}
                <div className="premium-stat-card">
                    <div className="growth-badge positive">+18.2%</div>
                    <div className="premium-icon-box green">
                        <FiUserCheck className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">Active Users</span>
                            <div className="p-dot green"></div>
                        </div>
                        <span className="p-value">{stats.active}</span>
                    </div>
                    <div className="wave-bg"></div>
                </div>

                {/* New Members */}
                <div className="premium-stat-card">
                    <div className="growth-badge positive">+32%</div>
                    <div className="premium-icon-box orange">
                        <FiUserPlus className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">New This Month</span>
                            <div className="p-dot orange"></div>
                        </div>
                        <span className="p-value">{stats.newThisMonth}</span>
                    </div>
                    <div className="wave-bg"></div>
                </div>
            </div>

            {/* Controls Row */}
            <div className="cust-controls-row">
                <div className="filter-group">
                    {['all', 'customer', 'admin'].map(r => (
                        <button
                            key={r}
                            className={`filter-btn ${roleFilter === r ? 'active' : ''}`}
                            onClick={() => setRoleFilter(r)}
                        >
                            {r === 'all' ? 'All Roles' : r.charAt(0).toUpperCase() + r.slice(1)}
                        </button>
                    ))}
                </div>
                <div className="cust-search-wrap">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search member name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button 
                    className="btn-create-admin" 
                    onClick={() => {
                        setRegisterStep(1);
                        setRegError('');
                        setShowRegisterModal(true);
                    }}
                >
                    <FiUserPlus /> CREATE A NEW ADMIN
                </button>
            </div>

            {/* Customer Directory Table */}
            <div className="cust-table-container">
                <table className="adr-cust-table">
                    <thead>
                        <tr>
                            <th>PROFILE</th>
                            <th>USER ID</th>
                            <th>ROLE</th>
                            <th>STATUS</th>
                            <th>JOINED</th>
                            <th>PHONE</th>
                            <th>ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            [1, 2, 3, 4, 5].map(i => <tr key={i} className="skeleton-row"><td colSpan="7" style={{ height: '70px', background: '#f8fafc' }}></td></tr>)
                        ) : filteredUsers.map(user => (
                            <tr key={user._id}>
                                <td>
                                    <div className="profile-cell">
                                        <div className="prof-avatar" style={{ background: `linear-gradient(135deg, ${getAvatarColor(user.fullName)}, #3b82f6)` }}>
                                            {user.fullName?.charAt(0) || 'U'}
                                        </div>
                                        <div className="prof-info">
                                            <span className="prof-name">{user.fullName || 'Member'}</span>
                                            <span className="prof-email">{user.email}</span>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span className="order-uuid">#{user._id.substring(18)}</span>
                                </td>
                                <td>
                                    <span className={`role-badge ${user.role}`}>
                                        {user.role === 'admin' ? <FiShield /> : <FiUsers />}
                                        {user.role}
                                    </span>
                                </td>
                                <td>
                                    <span className={`status-pill ${user.isActive ? 'active' : 'inactive'}`}>
                                        {user.isActive ? <FiCheckCircle /> : <FiXCircle />}
                                        {user.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    <span className="date-text">{formatDate(user.createdAt)}</span>
                                </td>
                                <td>
                                    <span className="phone-text">{user.phone || '—'}</span>
                                </td>
                                <td>
                                    <div className="table-actions-row">
                                        <button
                                            className="btn-view-cust"
                                            onClick={() => {
                                                setSelectedUser(user);
                                                setShowDrawer(true);
                                            }}
                                        >
                                            <FiEye /> Profile
                                        </button>
                                        <button
                                            className="btn-quick-delete"
                                            onClick={() => initiateDelete(user._id)}
                                            title="Delete Member"
                                        >
                                            <FiTrash2 />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Customer Detail Drawer */}
            {showDrawer && selectedUser && (
                <div className="cust-drawer-overlay" onClick={() => setShowDrawer(false)}>
                    <div className="cust-drawer" onClick={e => e.stopPropagation()}>
                        <div className="drawer-header">
                            <div className="d-title-area">
                                <span className="d-member-tag">MEMBER DIRECTORY</span>
                                <h2>Profile Insights</h2>
                            </div>
                            <button className="close-drawer" onClick={() => setShowDrawer(false)}><FiX /></button>
                        </div>

                        <div className="drawer-body">
                            {/* User Hero Section */}
                            <div className="user-profile-hero">
                                <div className="hero-avatar" style={{ background: `linear-gradient(135deg, ${getAvatarColor(selectedUser.fullName)}, #3b82f6)` }}>
                                    {selectedUser.fullName?.charAt(0) || 'U'}
                                </div>
                                <div className="hero-meta">
                                    <h3 className="hero-name">{selectedUser.fullName}</h3>
                                    <span className="hero-join-date"><FiCalendar /> Joined {formatDate(selectedUser.createdAt)}</span>
                                </div>
                            </div>

                            <div className="drawer-info-grid">
                                <div className="info-column">
                                    {/* Personal Info */}
                                    <div className="section-card">
                                        <div className="section-header"><FiUserCheck /> Contact Credentials</div>
                                        <div className="info-item">
                                            <span className="info-label">Email Address</span>
                                            <span className="info-value">{selectedUser.email}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Mobile Phone</span>
                                            <span className="info-value">{selectedUser.phone || 'Not Provided'}</span>
                                        </div>
                                    </div>

                                    {/* Account Metadata */}
                                    <div className="section-card">
                                        <div className="section-header"><FiActivity /> Administrative Data</div>
                                        <div className="info-item">
                                            <span className="info-label">System ID</span>
                                            <span className="info-value">#{selectedUser._id}</span>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Last Synchronization</span>
                                            <span className="info-value">{formatDate(selectedUser.updatedAt)}</span>
                                        </div>
                                    </div>

                                    {/* Management Status */}
                                    <div className="section-card">
                                        <div className="section-header"><FiShield /> Access & Status</div>
                                        <div className="management-row">
                                            <div className={`manage-badge active`}>
                                                <FiCheckCircle /> {selectedUser.isActive ? 'Active Member' : 'Inactive'}
                                            </div>
                                            <div className={`manage-badge admin`}>
                                                <FiShield /> {selectedUser.role.toUpperCase()}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="profile-actions-stack">
                                        <button className="btn-edit-profile">
                                            <FiMail /> Dispatch Email
                                        </button>
                                        <button
                                            className="btn-delete-member"
                                            onClick={() => initiateDelete(selectedUser._id)}
                                        >
                                            <FiTrash2 /> Terminate Membership
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ════════════════════════════════════════════════════════
                ADMINISTRATIVE MODALS
                ════════════════════════════════════════════════════════ */}

            {/* Confirm Deletion Modal */}
            {showConfirmModal && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-card">
                        <div className="modal-icon-ring warning">
                            <FiAlertTriangle />
                        </div>
                        <h3>Security Confirmation</h3>
                        <p>Are you absolutely sure you want to permanently remove this member? This action cannot be reversed.</p>
                        <div className="modal-btn-group">
                            <button
                                className="modal-btn cancel"
                                onClick={() => setShowConfirmModal(false)}
                                disabled={isDeleting}
                            >
                                Negative, Cancel
                            </button>
                            <button
                                className="modal-btn confirm-delete"
                                onClick={confirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Terminating...' : 'Yes, Delete Member'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Deletion Success Modal */}
            {showSuccessModal && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-card">
                        <div className="modal-icon-ring success">
                            <FiCheckCircle />
                        </div>
                        <h3>Purge Successful</h3>
                        <p>The member credentials have been permanently removed from the boutique directory.</p>
                    </div>
                </div>
            )}
            {/* Create Admin Modal with Stepper */}
            {showRegisterModal && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-card register-admin-card">
                        <div className="modal-close-x" onClick={() => setShowRegisterModal(false)}><FiX /></div>
                        
                        <div className="reg-modal-header">
                            <h3>Administrative Onboarding</h3>
                            <p>Register a new elite member to the management team.</p>
                        </div>

                        <div className="reg-stepper">
                            <div className={`reg-step-dot ${registerStep >= 1 ? 'active' : ''}`}>1</div>
                            <div className={`reg-step-line ${registerStep >= 2 ? 'active' : ''}`}></div>
                            <div className={`reg-step-dot ${registerStep >= 2 ? 'active' : ''}`}>2</div>
                        </div>

                        {regError && <div className="reg-error-msg">{regError}</div>}

                        <form onSubmit={handleRegisterSubmit} className="reg-admin-form">
                            {registerStep === 1 ? (
                                <div className="reg-step-content anim-slide-in">
                                    <div className="reg-input-group">
                                        <label>Full Name</label>
                                        <div className="reg-input-wrap">
                                            <FiUsers className="reg-icon" />
                                            <input 
                                                type="text" 
                                                placeholder="Enter full name"
                                                value={regFormData.fullName}
                                                onChange={(e) => setRegFormData({...regFormData, fullName: e.target.value})}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="reg-input-group">
                                        <label>Work Email</label>
                                        <div className="reg-input-wrap">
                                            <FiMail className="reg-icon" />
                                            <input 
                                                type="email" 
                                                placeholder="Enter official email"
                                                value={regFormData.email}
                                                onChange={(e) => setRegFormData({...regFormData, email: e.target.value})}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="reg-step-content anim-slide-in">
                                    <div className="reg-input-group">
                                        <label>Secure Password</label>
                                        <div className="reg-input-wrap">
                                            <FiShield className="reg-icon" />
                                            <input 
                                                type="password" 
                                                placeholder="Enter secure password"
                                                value={regFormData.password}
                                                onChange={(e) => setRegFormData({...regFormData, password: e.target.value})}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="reg-notice">
                                        <FiAlertTriangle />
                                        <span>New admins will have full directory access.</span>
                                    </div>
                                </div>
                            )}

                            <div className="reg-actions">
                                {registerStep === 2 && (
                                    <button type="button" className="reg-btn-back" onClick={() => setRegisterStep(1)}>
                                        Back
                                    </button>
                                )}
                                <button type="submit" className="reg-btn-submit" disabled={regLoading}>
                                    {regLoading ? 'Processing...' : (registerStep === 1 ? 'Next Step' : 'Authorize Admin')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminCustomers;


